import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import EmployeeService, { Employee } from '../../services/employee.service';
import DepartmentService from '../../services/department.service';
import { NumberTextField } from '../../components/common';

const EmployeeForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState<any[]>([]);
  const [formData, setFormData] = useState<Employee>({
    employeeNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    hireDate: new Date().toISOString().split('T')[0],
    employmentType: 'fullTime',
    status: 'active',
    currency: 'OMR',
  });

  useEffect(() => {
    fetchDepartments();
    if (isEdit && id) {
      fetchEmployee(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchDepartments = async () => {
    try {
      const response = await DepartmentService.getDepartments();
      setDepartments(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployee = async (employeeId: number) => {
    try {
      const response = await EmployeeService.getOne(employeeId);
      const employee = response.data?.data?.attributes;
      if (employee) {
        setFormData({
          ...employee,
          department: employee.department?.data?.id,
          // Strapi decimals often come back as strings; normalize for number inputs
          salary:
            employee.salary === null || employee.salary === undefined || employee.salary === ''
              ? undefined
              : Number(employee.salary),
        });
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await EmployeeService.update(parseInt(id), formData);
      } else {
        await EmployeeService.create(formData);
      }
      navigate('/employees');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Employee' : 'Create Employee'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Employee Number"
                name="employeeNumber"
                value={formData.employeeNumber}
                onChange={handleChange}
                disabled={isEdit}
              />
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                label="Gender"
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
              <TextField
                fullWidth
                select
                label="Department"
                name="department"
                value={formData.department || ''}
                onChange={handleChange}
              >
                <MenuItem value="">None</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.attributes?.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Position"
                name="position"
                value={formData.position || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                required
                label="Hire Date"
                name="hireDate"
                type="date"
                value={formData.hireDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                label="Employment Type"
                name="employmentType"
                value={formData.employmentType || 'fullTime'}
                onChange={handleChange}
              >
                <MenuItem value="fullTime">Full Time</MenuItem>
                <MenuItem value="partTime">Part Time</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="intern">Intern</MenuItem>
              </TextField>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status || 'active'}
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="onLeave">On Leave</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="terminated">Terminated</MenuItem>
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                label="Salary"
                name="salary"
                value={formData.salary || ''}
                onChange={handleChange}
                onValueChange={(val) => setFormData((prev) => ({ ...prev, salary: val }))}
                inputProps={{ step: '0.01', min: '0' }}
              />
              <TextField
                fullWidth
                label="Currency"
                name="currency"
                value={formData.currency || 'USD'}
                onChange={handleChange}
              />
            </Stack>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Address"
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              HR Report Information
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Employee ID Type"
                name="employeeIdType"
                value={formData.employeeIdType || ''}
                onChange={handleChange}
                placeholder="C for Civil ID"
                helperText="Usually 'C' for Civil ID"
              />
              <TextField
                fullWidth
                label="Employee ID"
                name="employeeId"
                value={formData.employeeId || ''}
                onChange={handleChange}
                placeholder="e.g., 15102509"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Reference Number"
                name="referenceNumber"
                value={formData.referenceNumber || ''}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Employee BIC Code"
                name="employeeBicCode"
                value={formData.employeeBicCode || ''}
                onChange={handleChange}
                placeholder="e.g., BMUSOMRX"
                helperText="Bank Identifier Code"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Employee Account"
                name="employeeAccount"
                value={formData.employeeAccount || ''}
                onChange={handleChange}
                placeholder="Bank account number"
                helperText="Bank account number for salary payment"
              />
              <TextField
                fullWidth
                select
                label="Salary Frequency"
                name="salaryFrequency"
                value={formData.salaryFrequency || 'M'}
                onChange={handleChange}
              >
                <MenuItem value="M">Monthly (M)</MenuItem>
                <MenuItem value="W">Weekly (W)</MenuItem>
                <MenuItem value="B">Bi-weekly (B)</MenuItem>
                <MenuItem value="D">Daily (D)</MenuItem>
              </TextField>
            </Stack>

            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/employees')}
                disabled={loading}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default EmployeeForm;
